"use client";

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormContext } from "@/contexts/FormDataContext";
import { useSchema } from "@/contexts/FormSchemaContext";
import { GlobalContext } from "@/contexts/SiteDataContext";
import ActionButtons from "../ActionButtons";
import { getFormNameByPdfId } from "@/utils/Form/formUtils";
import { useFormUtils } from "@/hooks/useFormUtils";
import { getPersistedFormValues, persistFormValues, usePersistForm } from "@/hooks/usePersistForm";
import { FormStepGroupType, FormStepType } from "@/data/forms/types";
import { FormTitle } from "./components/FormTitle";
import { FormDisclaimer } from "./components/FormDisclaimer";
import { FormGroup } from "./components/FormGroup";
import FormAlertsPopups from "../Alerts";
import { AddonsData } from "../Fields/types";
import useDependableFields from "@/hooks/useDependableFields";
import Loading from "@/components/atoms/Loading";
import { formatDatesInObject } from "@/utils/dates";
import prepareAmplitudeData from "@/utils/usePrepareAmplitudeData";
import AmplitudeEventClient from "@/components/molecules/Tracking/AplitudeEventClient";
import { amplitudeTrackEvent } from "@/services/Amplitude";
import { calculateTotal } from "@/utils/addons";
import { sendAddToCartEvent } from "@/services/DataLayer/events";
import * as log from "@/actions/logger";
import "./styles.scss";

type BasicObjectType = Record<string, any>;

const Step = () => {
  const { formSchema: formSteps, pdfId } = useSchema();
  const { formData, formDetails, step, setValidatedForm, setStep, setCompletedSteps } =
    useContext(FormContext)!;
  const { t, sourceURL } = useContext(GlobalContext).context;
  const [loadingRequestApi, setLoadingRequestApi] = useState<boolean>(false);
  const [nextStepTriggerCount, setNextStepTriggerCount] = useState(0);
  const formName = pdfId && getFormNameByPdfId(pdfId);

  const {
    isGroupVisible,
    setPathnameWithCurrentStep,
    handleBack,
    handleNext,
    onSubmit,
    isEditingForm,
    getDefaultValues,
    getStepFields,
  } = useFormUtils();

  const currentStep: FormStepType | null = formSteps ? formSteps[step] : null;

  const stepFields = currentStep ? getStepFields(currentStep) : [];

  const defaultValues: BasicObjectType = getDefaultValues(formSteps);

  const getInitialValues = () => {
    const persistedValues = formName ? getPersistedFormValues(formName) : {};
    const { addons: _persistedAddons, ...persistedFieldValues } = persistedValues;

    return { ...defaultValues, ...formData, ...persistedFieldValues };
  };

  const initialValues: BasicObjectType = getInitialValues();

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, touchedFields, isValid, isSubmitted },
    getValues,
    setError,
    clearErrors,
    watch,
    setValue,
    setFocus,
    reset,
    unregister,
    getFieldState,
  } = useForm({
    mode: "onBlur",
    shouldUnregister: false,
    defaultValues: formatDatesInObject(initialValues),
  });

  const emailAddress = watch("emailAddress");
  const emailAddressValidation = watch("emailAddressValidation");

  useEffect(() => {
    if (emailAddressValidation) {
      trigger("emailAddressValidation");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAddress, trigger]);

  useEffect(() => {
    const nextInitialValues = getInitialValues();
    reset(formatDatesInObject(nextInitialValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, formData, step, formName]);

  const formValues = useDependableFields(currentStep, watch);
  const values = getValues();

  useEffect(() => {
    if (!currentStep || !formName) return;
    const { eventName, eventOptions } = prepareAmplitudeData(
      step,
      currentStep.name,
      formName,
      sourceURL
    );
    amplitudeTrackEvent(eventName, eventOptions);
  }, [step, currentStep, formName, sourceURL]);

  useEffect(() => {
    if (formSteps && formSteps.length > step) {
      setPathnameWithCurrentStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSteps, step]);

  // Send DataLayer event sendAddToCartEvent on first form step
  const addToCartFiredRef = useRef(false);

  useEffect(() => {
    if (formName == null || formDetails?.price == null) return;
    if (step !== 0) {
      addToCartFiredRef.current = false;
      return;
    }
    if (addToCartFiredRef.current) return;
    addToCartFiredRef.current = true;

    const allAddons = formDetails.addons ?? [];
    const activeAddons = allAddons.filter((addon: AddonsData) => addon.value === "1");
    const totalPrice = calculateTotal(formDetails.price, allAddons);
    sendAddToCartEvent(
      formName,
      activeAddons,
      formDetails.price,
      parseFloat(totalPrice.toFixed(2))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, formName, formDetails?.price]);

  useEffect(() => {
    setValidatedForm(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const saveCurrentStepValues = () => {
    if (!formName) return;

    persistFormValues(getValues(), `FORM_DATA_${formName}`, { activeFields: stepFields });
  };

  usePersistForm({
    value: values,
    localStorageKey: formName ? `FORM_DATA_${formName}` : "",
    activeFields: stepFields,
  });

  useEffect(() => {
    if (formSteps && formSteps.length <= step) {
      setStep(0);
      setCompletedSteps([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSteps, step]);

  const watchedBirthDate = watch("birthDate");
  const watchedPassportIssuanceDate = watch("passportIssuanceDate");
  const watchedPassportIssuanceDateOptional = watch("passportIssuanceDate--optional");
  const watchedLimitedValidityPassport = watch("limitedValidityPassport");
  const watchedCardIssuanceDate = watch("cardIssuanceDate");
  const watchedCardIssuanceDateOptional = watch("cardIssuanceDate--optional");
  const watchedNameChange = watch("nameChange");
  const watchedIncorrectPrintedInfo = watch("incorrectPrintedInfo");
  const watchedDocumentType2 = watch("documentType2");

  const alertFieldsToWatch = useMemo(
    () => ({
      birthDate: watchedBirthDate,
      passportIssuanceDate: watchedPassportIssuanceDate,
      passportIssuanceDateOptional: watchedPassportIssuanceDateOptional,
      limitedValidityPassport: watchedLimitedValidityPassport,
      cardIssuanceDate: watchedCardIssuanceDate,
      cardIssuanceDateOptional: watchedCardIssuanceDateOptional,
      nameChange: watchedNameChange,
      incorrectPrintedInfo: watchedIncorrectPrintedInfo,
      documentType2: watchedDocumentType2,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      watchedBirthDate,
      watchedPassportIssuanceDate,
      watchedPassportIssuanceDateOptional,
      watchedLimitedValidityPassport,
      watchedCardIssuanceDate,
      watchedCardIssuanceDateOptional,
      watchedNameChange,
      watchedIncorrectPrintedInfo,
      watchedDocumentType2,
    ]
  );

  if (!formSteps || !currentStep) return null;

  const handleSubmitWithLoader = async (data: BasicObjectType) => {
    setLoadingRequestApi(true);
    try {
      await onSubmit({ ...data });
    } catch (error) {
      log.error({ err: error, module: "Step" }, "Submission error");
      setLoadingRequestApi(false);
    } finally {
      const isLastStep = step === formSteps.length - 1;
      if (!isLastStep) setLoadingRequestApi(false);
    }
  };

  const formMethods = {
    register,
    unregister,
    touchedFields,
    isSubmitted,
    watch,
    trigger,
    setValue,
    errors,
    setError,
    clearErrors,
    formValues,
    values,
  };

  return (
    <div className="Step u-box">
      <FormAlertsPopups
        fieldsToWatch={alertFieldsToWatch}
        form={formName || ""}
        touchedFields={touchedFields}
        setValue={setValue}
      />
      <form onSubmit={handleSubmit(handleSubmitWithLoader)}>
        <FormTitle
          title={currentStep.title}
          subLabel={currentStep.subLabel}
          t={t}
        />
        <FormDisclaimer
          disclaimer={currentStep.disclaimer}
          t={t}
        />
        {currentStep?.groups.map((group: FormStepGroupType, index: number) =>
          isGroupVisible({ groupName: group.name, formValues, formSteps }) ? (
            <FormGroup
              key={`${index}-${group.name}`}
              t={t}
              group={group}
              formValues={formValues}
              formSteps={formSteps}
              formMethods={formMethods}
              nextStepTriggerCount={nextStepTriggerCount}
            />
          ) : null
        )}
        {loadingRequestApi && <Loading full />}
        <ActionButtons
          isEditingForm={isEditingForm}
          currentStep={step}
          totalSteps={formSteps.length}
          handleBack={() => {
            saveCurrentStepValues();
            handleBack(step, setStep);
          }}
          handleNext={async () => {
            saveCurrentStepValues();
            const advanced = await handleNext(
              step,
              setStep,
              setFocus,
              stepFields,
              trigger,
              getFieldState,
              getValues()
            );
            // Only count failed Next attempts on the current step so collapsibles
            // stay closed when landing on a new step after a successful advance.
            if (!advanced) {
              setNextStepTriggerCount((attempt) => attempt + 1);
            } else {
              setNextStepTriggerCount(0);
            }
          }}
          applicationValues={formData}
          pdfId={pdfId}
          isValid={isValid}
        />
      </form>
    </div>
  );
};

export default Step;
